import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import es from './locales/es.json';
import { SUPPORTED_LOCALES, isSupportedLocale } from './config/locales';

/**
 * Default locale resolved at build time. The live Spanish Vercel project sets
 * REACT_APP_LOCALE=es so its root (/) renders Spanish; the main project leaves
 * it unset and defaults to English. At runtime the route prefix (/jp, /es, …)
 * overrides this via ensureLocale().
 */
export const ENV_DEFAULT_LOCALE = isSupportedLocale(process.env.REACT_APP_LOCALE)
    ? process.env.REACT_APP_LOCALE
    : 'en';

/**
 * Lazy-loaded locale bundles. English and Spanish are bundled eagerly (en for
 * the conversion-critical default funnel, es so the existing Spanish project has
 * no flash). New languages are dynamically imported on first use so they never
 * weigh down the default bundle.
 */
const lazyLoaders: Partial<Record<string, () => Promise<{ default: Record<string, unknown> }>>> = {
    jp: () => import('./locales/jp.json'),
};

i18n
    .use(initReactI18next)
    .init({
        resources: {
            en: { translation: en },
            es: { translation: es },
        },
        lng: ENV_DEFAULT_LOCALE,
        fallbackLng: 'en',
        interpolation: {
            escapeValue: false,
        },
    });

/**
 * Ensure the given locale's translations are loaded, then switch to it.
 * Unsupported values fall back to English. Safe to call repeatedly.
 */
export async function ensureLocale(language: string): Promise<void> {
    const target = isSupportedLocale(language) ? language : 'en';

    if (!i18n.hasResourceBundle(target, 'translation')) {
        const loader = lazyLoaders[target];
        if (loader) {
            try {
                const mod = await loader();
                i18n.addResourceBundle(target, 'translation', mod.default, true, true);
            } catch {
                // Bundle failed to load — fall through and let fallbackLng (en) cover it.
            }
        }
    }

    if (i18n.language !== target) {
        await i18n.changeLanguage(target);
    }
}

export { SUPPORTED_LOCALES };
export default i18n;
